import EventEmitter from "events";
import { DateTime } from "luxon";

import { ITileCoordinates } from "../tileCoordinates";


/**
 * Interface for cache implementations
 */
export interface ITileCache extends EventEmitter {
    /**
     * Receives a tile as its base64 encoded data url.
     */
    getTileAsDataUrl(tileCoordinates: ITileCoordinates, urlTemplate: string, urlSubdomains?: string[]): Promise<string>;

    /**
     * Clears the whole cache.
     */
    purgeStore(): Promise<boolean>;

    getInfo(): Promise<ITileCacheInfo>;
}

export interface ITileCacheInfo {
    storageSizeBytes: number;
    numberOfTiles: number;
}

/**
 * Interface for cache seeder action test methods
 */
export interface ITileCacheSeederActions {
    canCancel: (op: ITileCacheSeedOperation) => boolean;
    cancelOperation: (opId: string) => Promise<ITileCacheSeedOperation>;

    canRemove: (op: ITileCacheSeedOperation) => boolean;
    removeOperation: (opId: string) => Promise<ITileCacheSeedOperation>;
}

/**
 * Interface for cache seeder implementations
 */
export interface ITileCacheSeeder extends EventEmitter, ITileCacheSeederActions {
    readonly id: string;
    readonly name: string;
    isSupported: () => boolean; 
    maxInProgressDownloads(): number;
    getSeedHistory(): Promise<ITileCacheSeedOperation[]>;
    cullSeedHistory(currentDate: DateTime): Promise<void>;
    seedFromBounds: (crs: L.CRS, boundingBox: L.LatLngBounds, maxZoom: number, minZoom: number, urlTemplate: string, urlSubdomains?: string[] | null, thumbUrl?: string | null) => Promise<ITileCacheSeedOperation>;
}

export interface ITileCacheSeedOperation {
    id: string;
    seederId: string;
    status: TileCacheSeedOperationStatus;
    error?: Error | null;
    startedAt: number,
    numTotal: number;
    downloadedBytes?: number | null;
    downloadedTotal?: number | null;
    thumbUrl?: string | null;
    requestUrls?: { internalUrl: string; requestUrl: string; }[];
}

export enum TileCacheSeedOperationStatus {
    InProgress = 1,
    Failed = 2,
    Succeeded = 3,
    Cancelled = 4
}

export type TileCacheSeedOperationStartedEvent = {
    type: TileCacheSeedOperationMessageType;
    op: ITileCacheSeedOperation
}

export type TileCacheSeedOperationChangeEvent = {
    type: TileCacheSeedOperationMessageType;
    opId: string;
    status?: TileCacheSeedOperationStatus;
    error?: Error | null;
    downloadedBytes?: number | null;
    downloadedTotal?: number | null;
}

export enum TileCacheSeedOperationMessageType {
    starting = 'opstarting',
    statusChange = 'opstatuschange',
    progressChange = 'opprogresschange',
    showDetails = 'opshowdetails',
    removed = 'opremoved'
}